import { LabelValue } from '../../platform/types/LabelValue';
import { ApiCall } from '../ApiClient';
import { ResultData } from '../ApiTypes';

const get = <TResponse>(def: Omit<ApiCall<never, TResponse>, 'method'>): ApiCall<never, TResponse> =>
    Object.assign(def, { method: 'get' });

const post = <TResponse>(def: Omit<ApiCall<any, TResponse>, 'method'>): ApiCall<any, TResponse> =>
    Object.assign(def, { method: 'post' });

export interface JiraPagedData<T = any> {
    maxResults: number;
    startAt: number;
    total: number;
    isLast: boolean;
    items: T[];
}

export interface JiraPagedSearchOpts {
    jql?: string;
    sort?: string;
    startAt?: number;
}

export interface JiraProjectLiteData {
    id: string;
    key: string;
    name: string;
    avatarUrl: string;
}

export interface JiraIssueTypeLiteData {
    id: string;
    name: string;
    iconUrl: string;
}

export interface JiraStatusLiteData {
    id: string;
    name: string;
    iconUrl: string;
    colorName: string;
}

export interface JiraPriorityLiteData {
    id: string;
    name: string;
    iconUrl: string;
}

export interface JiraTransitionData {
    id: string;
    name: string;
    to: JiraStatusLiteData;
}

export interface JiraUserLiteData {
    id: string;
    avatarUrl: string;
    displayName: string;
}

export interface JiraStatusData extends JiraStatusLiteData {
    description: string;
    untranslatedName: string;
    // statusCategory: {
    //     self: string,
    //     id: number,
    //     key: string,
    //     colorName: string,
    //     name: string,
    // };
    scope: {
        type: string,
        project: {
            id: string,
        },
    };
}

export interface JiraIssueTypeData extends JiraIssueTypeLiteData {
    description: string;
    subtask: boolean;
    statuses: JiraStatusData[];
}

export interface JiraPriorityData extends JiraPriorityLiteData {
    statusColor: string;
    description: string;
}

export interface JiraUserData extends JiraUserLiteData {
    accountType: string;
    emailAddress: string;
    timeZone: string;
    locale: string;
}

export interface JiraProjectData extends JiraProjectLiteData {
    id: string;
    key: string;
    name: string;
    description: string;
    avatarUrls: {
        '48x48': string,
        '24x24': string,
        '16x16': string,
        '32x32': string,
    };
    projectTypeKey: string;
    simplified: boolean;
    style: string;
    issueTypes: JiraIssueTypeData[];
    insight?: {
        totalIssueCount: number,
        lastIssueUpdateTime: string,
    };
}

export interface ExtendedJiraProjectData extends JiraProjectData {
    lead: JiraUserLiteData;
    issueTypes: JiraIssueTypeData[];
    // statuses: JiraStatusData[];
    // priorities: JiraPriorityData[];
    // users: JiraUserData[];
}

export interface JiraIssueLiteData {
    id: string;
    key: string;
    url: string;
    issueType: JiraIssueTypeLiteData;
    priority: JiraPriorityLiteData;
    status: JiraStatusLiteData;
    summary: string;
}

export interface JiraIssueData extends JiraIssueLiteData {
    projectId: string;
    parent?: JiraIssueLiteData;
    assignee: JiraUserLiteData;
    reporter: JiraUserLiteData;
    creator: JiraUserLiteData;
    custom: Record<string, any>;
    created: string;
}

export interface JiraLoadIssuesInput {
    boardId: string;
    jiraIssueIds: string[];
};

export type JiraIssueCreateInput = {
    localId: string,
    jiraProjectId: string,
    assignee?: { id: string },
    issueType?: { id: string },
    summary: string,
    description?: string,
};

export type JiraIssueCreateResult = {
    localId: string,
    ok: boolean,
    id?: string,
    key?: string,
    errors?: string[],
};

export type JiraIssuePatchAction = {
    type: 'set' | 'transition',
    params: Record<string, unknown>,
};

export type JiraIssuesPatchInput = {
    jiraProjectId: string,
    jiraIssueId: string,
    actions: JiraIssuePatchAction[],
};

export interface JiraFieldData {
    id: string;
    key: string;
    name: string;
    untranslatedName: string;
    custom: boolean;
    orderable: boolean;
    navigable: boolean;
    searchable: boolean;
    clauseNames: string[];
    scope?: {
        type: string,
        project?: {
            id: string,
        },
    };
    schema: {
        type: string,
        custom: string,
        customId: number,
    };
}

export interface JiraSprintLiteData {
    id: number;
    name: string;
}

export interface JiraSprintData extends JiraSprintLiteData {
    state: string;
    startDate: string;
    endDate: string;
    createdDate: string;
    originBoardId: number;
    goal: string;
}

export const jiraEndpoints = {

    fields: {

        list: (jiraProjectId: string) => get<JiraFieldData[]>({
            path: '/jira.fields.list',
            query: { jiraProjectId },
        }),

        suggest: (fieldName: string, search: string) => get<LabelValue[]>({
            path: '/jira.fields.suggest',
            query: { fieldName, search },
        }),
    },

    projects: {

        info: (projectId: string) => get<ExtendedJiraProjectData>({
            path: '/jira.projects.info',
            query: { projectId },
        }),

        list: (query: string) => get<JiraPagedData & { values: JiraProjectData[] }>({
            path: '/jira.projects.list',
            query: { query },
        }),

        issueTypes: {

            list: (jiraProjectId: string) => get<JiraIssueTypeData[]>({
                path: '/jira.projects.issueTypes.list',
                query: { jiraProjectId },
            }),

        },

        links: {

            create: (jiraProjectId: string, boardId: string) => post<ResultData>({
                path: '/jira.projects.links.create',
                body: { jiraProjectId, boardId },
            }),

        },

        sprints: {

            list: (jiraProjectId: string) => get<JiraSprintData[]>({
                path: '/jira.projects.sprints.list',
                query: { jiraProjectId },
            }),

        },

        users: {

            list: (jiraProjectId: string) => get<JiraUserLiteData[]>({
                path: '/jira.projects.users.list',
                query: { jiraProjectId },
            }),
        }
    },

    issues: {

        assignees: {
            list: (searchBy: 'project' | 'issueKey', value) => get<JiraUserLiteData[]>({
                path: '/jira.issues.assignees.list',
                query: searchBy === 'project' ? { jiraProjectId: value } : { jiraIssueKey: value },
            })
        },

        create: (body: JiraIssueCreateInput[]) => post<JiraIssueCreateResult[]>({
            path: '/jira.issues.create',
            body,
        }),

        patch: (body: JiraIssuesPatchInput[]) => post<ResultData>({
            path: '/jira.issues.patch',
            body,
        }),

        fields: (issueId: string) => get<any>({
            path: '/jira.issues.fields',
            query: { issueId },
        }),

        list: (opts: JiraPagedSearchOpts) => get<JiraPagedData<JiraIssueData>>({
            path: '/jira.issues.list',
            query: { ...opts },
        }),

        load: (body: JiraLoadIssuesInput) => post<any>({
            path: '/jira.issues.load',
            body,
        }),

        transitions: (issueId: string) => get<JiraTransitionData[]>({
            path: '/jira.issues.transitions',
            query: { issueId },
        }),
    },

    call: (url: string) => get<any>({
        path: '/jira.proxy',
        query: { url },
    }),
};
